"use client";

import { ArrowRight, Facebook, Instagram, Linkedin, Loader, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const contactBlocks = [{
  label: "CONSULTATION",
  primary: "+1 888 777 6655",
  primaryType: "phone",
  primaryValue: "tel:+18887776655",
  secondary: "Mon-Fri: 9am - 6pm EST",
  tertiary: "Global support available"
}, {
  label: "INQUIRIES",
  primary: "hello@hotelrevbaba.com",
  primaryType: "email",
  primaryValue: "mailto:hello@hotelrevbaba.com",
  secondary: "Revenue audit & tech stack requests",
  tertiary: "Response within 24 hours"
}, {
  label: "OFFICE",
  primary: "100 Hospitality Way",
  primaryType: "text",
  primaryValue: "",
  secondary: "New York, NY 10001",
  tertiary: "By appointment only"
}];
const socials = [{
  name: "Instagram",
  icon: Instagram,
  href: "#"
}, {
  name: "Twitter",
  icon: Twitter,
  href: "#"
}, {
  name: "Facebook",
  icon: Facebook,
  href: "#"
}, {
  name: "LinkedIn",
  icon: Linkedin,
  href: "#"
}];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-slate-200 bg-slate-50">
        <p className="font-default text-xs uppercase tracking-widest text-blue-700 mb-3">Message Sent</p>
        <p className="font-default text-slate-900 text-lg mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-slate-300 text-slate-900 hover:bg-blue-600 hover:text-white">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-slate-500">Full Name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Sunil Singh" className="rounded-lg border-0 border-b border-slate-300 bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-blue-600 text-slate-900 font-default placeholder:text-slate-400" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-slate-500">Email Address</FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="sunil@hotel.com" className="rounded-lg border-0 border-b border-slate-300 bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-blue-600 text-slate-900 font-default placeholder:text-slate-400" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-slate-500">Phone Number</FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="+1 555-0000" className="rounded-lg border-0 border-b border-slate-300 bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-blue-600 text-slate-900 font-default placeholder:text-slate-400" />
              </FormControl>
              <p className="font-default italic text-xs text-slate-500 mt-1">Include country code for international inquiries.</p>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="contact_method" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-slate-500">Service Interest</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="rounded-lg border-0 border-b border-slate-300 bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-blue-600 text-slate-900 font-default w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="rounded-lg bg-white border-slate-200">
                  <SelectItem value="OTA">OTA Revenue Management</SelectItem>
                  <SelectItem value="Tech">Tech Implementation</SelectItem>
                  <SelectItem value="Audit">Revenue Audit</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-[0.2em] text-slate-500">Project Details</FormLabel>
            <FormControl>
              <Textarea {...field} rows={4} placeholder="Tell us about your property's revenue goals..." className="rounded-lg border-0 border-b border-slate-300 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-blue-600 text-slate-900 font-default placeholder:text-slate-400 resize-none min-h-[110px]" />
            </FormControl>
            <p className="font-default italic text-xs text-slate-500 mt-2">Provide details such as current occupancy, OTA challenges, etc.</p>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-200 bg-red-50">
          <p className="text-red-700 text-sm font-default">{submitError}</p>
        </div>}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4 border-t border-slate-200">
        <div className="flex items-center gap-3">
          <Checkbox id="subscribe" className="rounded-lg border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
          <Label htmlFor="subscribe" className="font-default italic text-sm text-slate-600 cursor-pointer">
            Subscribe to revenue insights
          </Label>
        </div>
        <Button type="submit" disabled={isSubmitting} className="rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-md h-12 px-8 font-default uppercase tracking-[0.2em] text-xs">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending
            </> : <>
              Send Inquiry
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
      </div>

      <p className="font-default italic text-xs text-slate-500 pt-2">
        Your inquiry is handled directly by our expert team. Confidentiality guaranteed.
      </p>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3336" id="revenuejourneycontact" className="bg-slate-50 text-slate-900 py-24 md:py-[200px] px-6 md:px-12 lg:px-16 overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-16 md:mb-24">
          <span className="font-default text-[11px] uppercase tracking-[0.3em] text-blue-700">
            02 — CONTACT
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 flex flex-col">
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl text-slate-900 leading-[0.95] mb-12 tracking-tight">
              Maximize your<br />hotel revenue.
            </h2>

            <div className="flex flex-col">
              {contactBlocks.map((block, i) => <div data-index={i} key={block.label} className="py-8 border-t border-slate-200 first:border-t-0 first:pt-0">
                  <p className="font-default text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-4">
                    {block.label}
                  </p>
                  {block.primaryType === "phone" || block.primaryType === "email" ? <Link to={block.primaryValue} className="font-default text-base text-blue-700 hover:text-blue-900 transition-colors block mb-2">
                      {block.primary}
                    </Link> : <p className="font-default text-base text-slate-900 mb-2">{block.primary}</p>}
                  <p className="font-default text-sm text-slate-600">{block.secondary}</p>
                  <p className="font-default text-sm text-slate-600">{block.tertiary}</p>
                </div>)}
            </div>

            <div className="flex items-center gap-6 mt-12 pt-8 border-t border-slate-200">
              {socials.map((social, i) => {
              const Icon = social.icon;
              return <Link data-index={i} key={social.name} to={social.href} className="text-slate-600 hover:text-blue-600 transition-colors">
                    <Icon className="size-[22px]" strokeWidth={1} />
                  </Link>;
            })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute -top-2 -right-2 w-12 h-px bg-blue-600"></div>
              <div className="absolute -top-2 -right-2 w-px h-12 bg-blue-600"></div>

              <div className="bg-white backdrop-blur-2xl border border-slate-200 shadow-lg p-8 md:p-12 lg:p-16 rounded-lg">
                <div className="mb-10 pb-6 border-b border-slate-200">
                  <p className="font-default text-sm uppercase tracking-[0.25em] text-slate-900">
                    CONSULTATION REQUEST
                  </p>
                  <p className="font-default italic text-sm text-slate-500 mt-2">
                    Connect with Sunil Singh for revenue strategy.
                  </p>
                </div>

                <FormProvider formKey="revenue_journey_form_contact" formId="revenue_journey_form_contact" sectionName="inquiry-channels" defaultValues={{
                name: "",
                email: "",
                phone: "",
                contact_method: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: false
                },
                contact_method: {
                  required: true
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Send Inquiry" successMessage="Your inquiry has been received. We will reach out shortly." errorMessage="We were unable to send your inquiry. Please try again.">
                  <InquiryForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}